import { TAO_KOE, EN_KO, TaoConsole, DynamicYieldAPI } from './../libs';

import RealtimeFilters from './realtime-filters.js';

import { getCurrentCurrency } from './currency.js';

import DYCarouselItem from './productcarousel-item';

class DYCarousel {
	static lazyload = true;

	static Promises = [];

	// Batch attribute changes so multiple setAttribute() calls trigger only 1 rerender per frame.
	static PendingRerenders = new Set();
	static RerenderRafId = null;

	static DEBUG = false;

	static __frame = 0;
	static __frameStarted = false;
	static __lastLogT = 0;

	static startFrameCounter() {
		if ( DYCarousel.__frameStarted ) {
			return;
		}
		DYCarousel.__frameStarted = true;

		DYCarousel.__lastLogT = performance.now();

		const tick = () => {
			DYCarousel.__frame++;
			requestAnimationFrame( tick );
		};
		requestAnimationFrame( tick );
	}

	// Nice unified logger
	static dbg( label, extra = {} ) {
		if ( ! DYCarousel.DEBUG ) {
			return;
		}

		// Ensure counter runs
		DYCarousel.startFrameCounter();

		const t = performance.now();
		const dt = ( t - DYCarousel.__lastLogT ).toFixed( 2 );
		DYCarousel.__lastLogT = t;

		const base = `[DYCarousel] f=${ DYCarousel.__frame } t=${ t.toFixed( 2 ) } +${ dt }ms ${ label }`;
		const extraStr = Object.entries( extra ).map( ( [ k, v ] ) => `${ k }=${ v }` ).join( ' ' );

		// eslint-disable-next-line no-console
		console.log( extraStr ? `${ base } ${ extraStr }` : base );
	}

	static getDistinctChain = ( distinctGroup ) => {
		if ( ! distinctGroup ) {
			return Promise.resolve();
		}

		return Promise.allSettled(
			DYCarousel.Promises.map( ( [ Instance, promise ] ) => Instance.options.distinct === distinctGroup ? promise : Promise.resolve() ),
		);
	};

	static scheduleRerender( carouselEl ) {
		const cid = carouselEl.id || carouselEl.dataset.selector || 'unknown';

		DYCarousel.dbg( 'schedule', {
			cid,
			raf: DYCarousel.RerenderRafId ? 'set' : 'null',
		} );

		DYCarousel.PendingRerenders.add( carouselEl );
		if ( DYCarousel.RerenderRafId ) {
			return;
		}

		DYCarousel.RerenderRafId = requestAnimationFrame( () => {
			DYCarousel.dbg( 'flush', { targets: DYCarousel.PendingRerenders.size } );

			DYCarousel.RerenderRafId = null;

			const targets = Array.from( DYCarousel.PendingRerenders );
			DYCarousel.PendingRerenders.clear();

			targets.forEach( ( el ) => {
				const instance = DYCarousel.getInstance( el );
				instance.resetCarousel();
				instance.enqueueRender();
			} );
		} );
	}

	static Observer = new MutationObserver( ( mutationList ) => {
		for ( const mutation of mutationList ) {
			if ( 'attributes' === mutation.type ) {
				const carouselEl = mutation.target;
				const cid = carouselEl.id || carouselEl.dataset.selector || 'unknown';

				DYCarousel.dbg( 'MO', {
					cid,
					attr: mutation.attributeName,
					val: carouselEl.getAttribute( mutation.attributeName ),
				} );

				DYCarousel.scheduleRerender( carouselEl );
			}
		}
	} );

	static ObservableAttributes = [ 'data-widgetid', 'data-strategy', 'data-template', 'data-selector', 'data-action', 'data-maxresults', 'data-context', 'data-distinct', 'data-options', 'data-sizelink' ];

	static Item = DYCarouselItem;

	constructor( carouselEl ) {
		DYCarousel.startFrameCounter();
		this.carouselEl = carouselEl;

		// Setup necessary data.
		this.resetCarousel();
		this.enqueueRender();

		// Reload the carousel after attributes changed.
		DYCarousel.Observer.observe( this.carouselEl, {
			attributes: true,
			attributeFilter: DYCarousel.ObservableAttributes,
		} );
	}

	resetCarousel() {
		this.strategy = this.carouselEl.dataset.strategy || this.carouselEl.dataset.widgetid;
		this.template = this.carouselEl.dataset.template || 'carousel';

		// Setup data sent to API.
		this.action = this.carouselEl.dataset.action || 'default';
		if ( 'options' === this.action ) {
			try {
				let parsed = this.carouselEl.dataset.options || '{}';
				if ( 'base64' === this.carouselEl.dataset.oencode ) {
					parsed = atob( parsed );
				}
				this.options = JSON.parse( parsed ) || {};
			} catch {
				this.options = {};
			}
		} else {
			this.options = {};
		}

		// Legacy Maxresults.
		if ( 'maxresults' in this.carouselEl.dataset ) {
			this.options.maxProducts = parseInt( this.carouselEl.dataset.maxresults || 0 );
		}

		// Legacy Context.
		if ( 'context' in this.carouselEl.dataset ) {
			this.options.customParam = this.carouselEl.dataset.context;
		}

		// Legacy Distinct.
		if ( 'distinct' in this.carouselEl.dataset ) {
			const distinctGroup = String( this.carouselEl.dataset.distinct || '' );
			if ( distinctGroup ) {
				this.options.distinct = distinctGroup;
			}
		}

		// Add Skeleton.
		if ( 'listing' !== this.template ) {
			// Save Skeleton for later use.
			const skeletonEl = this.carouselEl.querySelector( '.mdl_productskeleton' );
			if ( skeletonEl ) {
				this.skeletonTemplateEl = skeletonEl.cloneNode( true );
			} else if ( this.skeletonTemplateEl ) {
				const containerEl = this.getContainerEl();
				containerEl.replaceChildren( ...Array.from( Array( 12 ) ).map( () => this.skeletonTemplateEl.cloneNode( true ) ) );
				this.setLoading( true );
			}
		}

		// Clear responseData.
		this.lastPromise = null;
		this.responseData = null;
		// In-flight request cache (prevents duplicate API calls on parallel renders)
		this.dataPromise = null;
	}

	enqueueRender() {
		const carouselEl = this.carouselEl;

		// Enqueue Render or Re-Render on Registration.
		if ( DYCarousel.lazyload ) {
			// If the inView class does not exist, the default Lazyload Observer should still wait for the element to come into view.
			if ( carouselEl.classList.contains( 'tao_productcarousel--in_view' ) ) {
				carouselEl.classList.remove( 'tao_productcarousel--in_view' );
			}

			EN_KO.functions.initSimpleLazyLoadOnScroll( carouselEl, 'tao_productcarousel--in_view', () => this.render() );
		} else {
			this.render();
		}
	}

	async getData() {
		// Check for dynamic yield.
		const DY = window.DY;
		const DYO = window.DYO;

		// In-flight cache: if a request is already running, reuse it.
		if ( this.dataPromise ) {
			return this.dataPromise;
		}

		// Return cached data (already resolved earlier).
		if ( this.responseData ) {
			return Promise.resolve( this.responseData );
		}

		// Create and cache the in-flight promise.
		this.dataPromise = ( async() => {
			if ( this.carouselEl.dataset.selector ) {
				const { selector, group } = this.carouselEl.dataset;
				const context = DY?.recommendationContext || null;
				const opts = await RealtimeFilters.getWidgetOpts( this.action, { context, ...this.options } );

				return DynamicYieldAPI.getChoices( selector, { group, context, ...opts } )
					.then( ( { choices } ) => {
						const choice = Object.values( choices ).find( ( { name } ) => name === selector );
						if ( choice && choice.variations.length > 0 ) {
							const variationData = choice.variations[ 0 ].payload.data;

							return {
								...variationData,
								slots: ( variationData.slots || [] ).map( ( { productData, ...slot } ) => {
									return {
										type: 'Campaign',
										item: {
											sku: slot.sku,
											...productData,
										},
										...slot,
									};
								} ),
							};
						}

						return Promise.reject(
							'Invalid response for selector ' + JSON.stringify( choice?.name || selector ) + '!',
						);
					} );
			}

			if ( 'function' !== typeof DYO?.recommendationWidgetData ) {
				return Promise.reject( 'Dynamic Yield not loaded!' );
			}

			return Promise.race( [
				// Load Data.
				new Promise( async( resolve, reject ) => {
					const opts = await RealtimeFilters.getWidgetOpts( this.action, this.options );
					const success = DYO.recommendationWidgetData( this.strategy, opts, ( error, data ) => {
						if ( error ) {
							reject( error );
						} else {
							resolve( data );
						}
					} );

					if ( ! success ) {
						reject( 'Invalid DYO Strategy ID: ' + this.strategy );
					}
				} ),
				// Cancel after 15 seconds.
				new Promise( ( resolve, reject ) => {
					setTimeout( () => reject( 'Timeout while waiting for response: ' + this.strategy ), 15000 );
				} ),
			] );
		} )();

		// Store resolved data in responseData; allow retry on failure.
		return this.dataPromise
			.then( ( data ) => {
				this.responseData = data;
				return data;
			} )
			.catch( ( err ) => {
				// If it failed, clear in-flight promise so later calls can retry.
				this.dataPromise = null;
				throw err;
			} );
	}

	getDataDistinct() {
		const promise = DYCarousel.getDistinctChain( this.options.distinct ).then( async() => await this.getData() );
		DYCarousel.Promises.push( [ this, promise ] );
		this.lastPromise = promise;

		return promise.then( ( data ) => {
			if ( this.lastPromise !== promise ) {
				// Do not resolve this promise, instead we wait for the latest promise to resolve instead.
				return new Promise( () => {} );
			}

			return data;
		} ).then( ( data ) => {
			// Exclude items with more than one group-id, so that it exist only once except chanel group_id.
			if ( this.options.distinct ) {
				data.slots = data.slots.filter( ( slot, index, self ) => {
					return self.findIndex( ( v ) => v.item.group_id === slot.item.group_id ) === index;
				} );

				DYCarousel.Promises.filter( ( [ Instance ] ) => Instance !== this && Instance.options.distinct === this.options.distinct && Instance.responseData ).forEach( ( [ Instance ] ) => {
					const groupIds = Instance.responseData.slots.map( ( slot ) => slot.item.group_id );
					data.slots = data.slots.filter( ( slot ) => ! groupIds.includes( slot.item.group_id ) );
				} );
			}

			// Slice down recommendationscarousel itemcount to nearest 3-dividable.
			// See https://business.acl.at/jira/browse/KOC-8341
			if ( 'recommendations' === this.template ) {
				data.slots = data.slots.slice( 0, Math.min( Math.floor( data.slots.length / 3 ) * 3, 6 ) );
			}

			return data;
		} );
	}

	getContainerEl() {
		return 'listing' === this.template ? this.carouselEl : this.carouselEl.querySelector( '.productcarousel__list_inner' );
	}

	render() {
		DYCarousel.dbg( 'render:start', {
			cid: this.carouselEl.id || this.carouselEl.dataset.selector || 'unknown',
			stack: new Error().stack?.split( '\n' ).slice( 0, 6 ).join( ' | ' ),
		} );

		const containerEl = this.getContainerEl();

		return this.getDataDistinct()
			.then( async( data ) => {
				return [ data, await Promise.race( [ window.ACL_USER_DATA || [] ] ).catch( () => [] ) ];
			} )
			.then( ( [ data, userdata ] ) => {
				const items = data.slots.map( function( item ) {
					return new DYCarouselItem( item );
				} );

				if ( items.length <= 0 ) {
					throw 'No items returned for DYO: ' + ( this.carouselEl.dataset.selector || this.strategy );
				}

				// Update Title HTML.
				if ( data.custom && data.custom.title ) {
					const titleEl = this.carouselEl.querySelector( '.productcarousel__title' );
					if ( titleEl ) {
						titleEl.innerText = data.custom.title;
					}
				}

				const start = Date.now();
				return Promise.all( items.map( ( item, index ) => item.render( {
					index,
					userdata,
					'template': this.template || 'carousel',
					'target': this.carouselEl.dataset.linkTarget || false,
					'sizeLink': this.carouselEl.dataset.sizelink || false,
				} ) ) ).then( ( contents ) => {
					TaoConsole.log( 'RENDER', Date.now() - start );
					containerEl.innerHTML = contents.join( '' );
					TaoConsole.log( 'APPEND', Date.now() - start );

					if ( ! this.carouselEl.dataset.selector ) {
						// Setup Tracking Parameters.
						containerEl.setAttribute( 'data-dy-widget-id', data.wId );
						containerEl.setAttribute( 'data-dy-feed-id', data.fId );

						// Setup Trackings.
						setTimeout( () => DYCarousel.trackWithDy( containerEl ), 100 );
					}

					// Setup Datalayer push.
					setTimeout( () => DYCarousel.trackWithGTM( items ), 100 );

					return data;
				} );
			} ).then( ( data ) => {
				this.setLoading( false );

				// Initialise Carousel.
				if ( 'listing' !== this.template ) {
					// Init carousel ( if needed ).
					this.carouselEl.dispatchEvent( new CustomEvent( 'carouselUpdateItems.sl', { detail: { data } } ) );
				}

				this.carouselEl.dispatchEvent( new CustomEvent( 'simple_lazyload.sl', { bubbles: true } ) );

				return data;
			} ).catch( ( error ) => {
				// eslint-disable-next-line no-console
				console.error( error );

				// Hide Carousel.
				this.carouselEl.classList.add( 'en_hidden' );

				// Hide Section above if necessary.
				const sectionEl = this.carouselEl.closest( '.mdl_blocksection.mdl_blocksection--carousel' );
				if ( sectionEl ) {
					sectionEl.classList.add( 'en_hidden' );
				}

				return false;
			} );
	}

	setLoading( isLoading = true ) {
		this.carouselEl.querySelectorAll( '.productcarousel__list' ).forEach( ( listEl ) => listEl.classList.toggle( 'is-loading', isLoading ) );
	}

	static trackWithGTM( items ) {
		const impressionList = items.map( function( item, index ) {
			return item.getTrackingData( index + 1 );
		} );

		if ( impressionList.length <= 0 ) {
			return;
		}

		// Limit number of impressions with each call ( there is a max of 8KB per event ).
		window.dataLayer = window.dataLayer || [];
		const chunkSize = 32;
		for ( let i = 0; i < impressionList.length; i += chunkSize ) {
			const chunk = impressionList.slice( i, i + chunkSize );
			window.dataLayer.push( {
				'event': 'productImpression',
				'ecommerce': {
					'currencyCode': getCurrentCurrency() || 'EUR',
					'impressions': chunk,
				},
			} );
		}
	}

	static trackWithDy( containerEl ) {
		const DYO = window.DYO;
		if ( 'function' === typeof DYO?.recommendations?.registerElements ) {
			DYO.recommendations.registerElements( containerEl );
		}
	}

	static getInstance( carouselEl ) {
		// Create new instance, if not exist.
		carouselEl.TAO_DYCarousel ??= new DYCarousel( carouselEl );
		return carouselEl.TAO_DYCarousel;
	}

	static renderInstance( carouselEl ) {
		const instance = DYCarousel.getInstance( carouselEl );
		return instance?.render();
	}
}

// Initialise Carousel.
const initProductCarousel = ( scopeEl = document ) => {
	const carouselEls = scopeEl.querySelectorAll( '.tao_productcarousel[data-source="dy"]' );
	if ( carouselEls.length <= 0 ) {
		return;
	}

	// Preload Carousel Data, if necessary.
	const groups = Array.from( carouselEls ).filter( ( carouselEl ) => carouselEl.matches( '[data-selector][data-group]' ) ).reduce( ( list, carouselEl ) => {
		const group = carouselEl.dataset.group;
		if ( group ) {
			list[ group ] ??= [];
			list[ group ].push( carouselEl.dataset.selector );
		}
		return list;
	}, {} );
	const DY = window.DY;
	const context = DY?.recommendationContext || null;
	Object.entries( groups ).forEach( ( [ group, selectors ] ) => {
		DynamicYieldAPI.preload( selectors, { group, context } );
	} );

	// Load Carousels.
	carouselEls.forEach( ( carouselEl ) => DYCarousel.getInstance( carouselEl ) );
};

// Init.
TAO_KOE.ready( 'productcarousel-dy.js', () => {
	// Initialise Carousel.
	initProductCarousel();
	document.addEventListener( 'lazyload.tao', ( evt ) => initProductCarousel( evt.target || document ) );
	document.addEventListener( 'finished_change_variant', ( evt ) => initProductCarousel( evt.target || document ) );
} );

// Expose.
TAO_KOE.DYCarousel = DYCarousel;
export default DYCarousel;
